import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import type { NetRevenueDashboard } from 'lumen/models';
import type { NetRevenueModel } from 'lumen/routes/hotel/net-revenue';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  COMPARISON_MODES,
  DATE_FORMAT,
  STAY_DATE_GRANULARITIES,
  type StayDateGranularity,
} from 'lumen/utils/constants/net-revenue-constants';
import {
  type CostColumnKey,
  DEFAULT_COLUMN_ORDER,
  DEFAULT_HIDDEN_COLUMNS,
} from 'lumen/utils/net-revenue/cost-columns';
import { hasCostProfileContent } from 'lumen/utils/net-revenue/cost-profile';
import {
  buildComparisonDatePickerRangeOptions,
  buildDatePickerRangeOptions,
  calculateComparisonLengthInDays,
  calculateComparisonToDateMoment,
  type ComparisonPickerList,
  type DateShortcutList,
  getComparisonFromDateMoment,
} from 'lumen/utils/net-revenue/date-pickers';
import type {
  CostProfileData,
  KpiData,
  StayDateData,
} from 'lumen/utils/net-revenue/types';

export default class HotelNetRevenueController extends Controller {
  declare model: NetRevenueModel;

  queryParams = ['comparisonFromDate', 'comparisonMode', 'fromDate', 'toDate'];

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intl: Services['intl'];

  @tracked comparisonFromDate: string | null = null;
  @tracked comparisonMode: string = COMPARISON_MODES.SWLY;
  @tracked costTableColumnOrder: CostColumnKey[] = [...DEFAULT_COLUMN_ORDER];
  @tracked costTableHiddenColumns: CostColumnKey[] = [
    ...DEFAULT_HIDDEN_COLUMNS,
  ];
  @tracked fromDate: string | null = null;
  @tracked stayDateGranularity: StayDateGranularity =
    STAY_DATE_GRANULARITIES.DAY;
  @tracked toDate: string | null = null;

  get comparisonDatePickerRangeOptions(): ComparisonPickerList[] {
    return buildComparisonDatePickerRangeOptions({
      vsComparisonTitle: this.intl.t('datepicker.options.vs_reference'),
      range: this.range,
      comparisonMode: this.comparisonMode,
      comparisonFromDateMoment: this.comparisonFromDateMoment,
      comparisonLengthInDays: this.comparisonLengthInDays,
    });
  }

  get comparisonFromDateMoment(): Dayjs {
    return getComparisonFromDateMoment(this.comparisonFromDate);
  }

  @cached
  get comparisonLabel(): string | null {
    if (!this.dashboard) {
      return null;
    }
    const keys: Record<string, string> = {
      SWLY: tKey('net_revenue.vs_swly'),
      SDLY: tKey('net_revenue.vs_sdly'),
      PP: tKey('net_revenue.vs_pp'),
      custom: tKey('net_revenue.vs_custom'),
    };
    const key = keys[this.comparisonMode];
    return key ? this.intl.t(key) : null;
  }

  get comparisonLengthInDays(): number {
    return calculateComparisonLengthInDays(this.range);
  }

  get comparisonRange(): { end: Dayjs | null; start: Dayjs } {
    return {
      start: this.comparisonFromDateMoment,
      end: this.comparisonToDateMoment,
    };
  }

  get comparisonToDateMoment(): Dayjs | null {
    return calculateComparisonToDateMoment({
      rangeStart: this.range.start,
      comparisonMode: this.comparisonMode,
      comparisonLengthInDays: this.comparisonLengthInDays,
      comparisonFromDateMoment: this.comparisonFromDateMoment,
    });
  }

  @cached
  get costProfileData(): CostProfileData | undefined {
    return this.dashboard?.buildCostProfile(
      this.referenceType,
      this.comparisonLabel,
    );
  }

  get currencyCode(): string {
    return (
      this.hotelAndMarketService.hotel.revenueInsightCurrency ??
      this.hotelAndMarketService.hotel.currencyCode
    );
  }

  get currentViewByOption():
    | { id: StayDateGranularity; label: string }
    | undefined {
    return this.viewByOptions.find(
      (option) => option.id === this.effectiveStayDateGranularity,
    );
  }

  get dashboard(): NetRevenueDashboard | undefined {
    return this.model.dashboard.last?.value ?? undefined;
  }

  get datePickerComparisonMaxDate(): Dayjs {
    return this.range.start.subtract(1, 'day');
  }

  get datePickerRangeOptions(): DateShortcutList[] {
    return buildDatePickerRangeOptions();
  }

  // Falls back to 'day' when the user previously picked 'month' but
  // has since narrowed the range so Month is no longer offered.
  get effectiveStayDateGranularity(): StayDateGranularity {
    if (
      this.stayDateGranularity === STAY_DATE_GRANULARITIES.MONTH &&
      !this.isMonthGranularityAllowed
    ) {
      return STAY_DATE_GRANULARITIES.DAY;
    }
    return this.stayDateGranularity;
  }

  get isCostDrilldownEmpty(): boolean {
    return !this.isLoading && !hasCostProfileContent(this.costProfileData);
  }

  get isError(): boolean {
    return Boolean(this.model.dashboard.last?.isError);
  }

  get isLoading(): boolean {
    return this.model.dashboard.isRunning;
  }

  // Month view is only meaningful when the selected range spans more
  // than one calendar month.
  get isMonthGranularityAllowed(): boolean {
    return !this.range.start.isSame(this.range.end, 'month');
  }

  @cached
  get kpiData(): KpiData | undefined {
    return this.dashboard?.buildKpiData(this.referenceType);
  }

  get range(): { end: Dayjs; start: Dayjs } {
    return {
      start: this.fromDate ? dayjs(this.fromDate) : dayjs().startOf('month'),
      end: this.toDate ? dayjs(this.toDate) : dayjs().endOf('month'),
    };
  }

  // The response keys comparison references by a lowercased period type
  // (e.g. "swly"), while the selected comparison mode is upper-cased.
  get referenceType(): string {
    return this.comparisonMode.toLowerCase();
  }

  get showCostTableLayout(): boolean {
    return !this.isError && !this.isCostDrilldownEmpty;
  }

  @cached
  get stayDateData(): StayDateData | undefined {
    return this.dashboard?.buildStayData(
      this.effectiveStayDateGranularity,
      this.referenceType,
      this.comparisonLabel,
    );
  }

  get viewByOptions(): { id: StayDateGranularity; label: string }[] {
    const options: { id: StayDateGranularity; label: string }[] = [
      { id: STAY_DATE_GRANULARITIES.DAY, label: this.intl.t('general.day') },
      { id: STAY_DATE_GRANULARITIES.WEEK, label: this.intl.t('general.week') },
    ];
    if (this.isMonthGranularityAllowed) {
      options.push({
        id: STAY_DATE_GRANULARITIES.MONTH,
        label: this.intl.t('general.month'),
      });
    }
    return options;
  }

  @action comparisonRangeChanged(
    range: { start: Dayjs },
    option: string,
  ): void {
    this.comparisonMode = option;
    this.comparisonFromDate = range.start.format(DATE_FORMAT);
  }

  @action costTableLayoutChanged(next: {
    columnOrder: CostColumnKey[];
    hiddenColumns: CostColumnKey[];
  }): void {
    this.costTableColumnOrder = next.columnOrder;
    this.costTableHiddenColumns = next.hiddenColumns;
  }

  @action rangeChanged(range: { end: Dayjs | null; start: Dayjs }): void {
    this.fromDate = range.start.format(DATE_FORMAT);
    this.toDate = (range.end ?? range.start).format(DATE_FORMAT);
  }

  @action stayDateGranularityChanged(
    option: { id: StayDateGranularity } | null,
  ): void {
    if (option) {
      this.stayDateGranularity = option.id;
    }
  }
}
